import { CheckCircle2, Circle } from "lucide-react";
import type { EmailToTaskSettings } from "@/services/email-to-task.service";
import { SectionBlock } from "../SectionBlock";

interface Step {
	label: string;
	detail: string;
	done: boolean | null; // null = manual step outside TaskHub
}

function buildSteps(s: EmailToTaskSettings): Step[] {
	const domain = s.inbound_address.split("@")[1] ?? "your domain";
	return [
		{
			label: `Resend: add ${domain} and enable Receiving`,
			detail: "Then add the MX and TXT records Resend shows to your DNS (Route 53).",
			done: null,
		},
		{
			label: "Resend: add the webhook",
			detail: "Use the Webhook URL above, event email.received, then save its signing secret below.",
			done: s.resend_webhook_secret_set,
		},
		{
			label: "AI Agent Hub: connect and pick an agent",
			detail: "Use a bot user's API key scoped to the extractor agent, with writes disabled.",
			done: Boolean(s.hub_base_url && s.hub_api_key_set && s.hub_agent_id),
		},
		{
			label: "Choose a default project",
			detail: "Tasks land here when the agent cannot match a project.",
			done: Boolean(s.default_project_id),
		},
		{
			label: "cron-job.org: schedule processing",
			detail: `Every 2 minutes, POST ${s.webhook_url.replace(/\/webhook$/, "/process")} with header X-Cron-Secret set to DIGEST_CRON_SECRET (same as the digest job).`,
			done: Boolean(s.last_processed_at),
		},
		{
			label: "Send a test email",
			detail: `Send an email to ${s.inbound_address} and check "Last email received" above.`,
			done: Boolean(s.last_webhook_at),
		},
	];
}

export function SetupChecklist({ settings }: { settings: EmailToTaskSettings }) {
	const steps = buildSteps(settings);
	const tracked = steps.filter((s) => s.done !== null);
	const doneCount = tracked.filter((s) => s.done).length;

	return (
		<SectionBlock
			title="Setup checklist"
			description={`${doneCount} of ${tracked.length} tracked steps complete.`}
		>
			<ol className="space-y-3">
				{steps.map((step) => (
					<li key={step.label} className="flex gap-3">
						{step.done ? (
							<CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-secondary" aria-label="Done" />
						) : (
							<Circle className="mt-0.5 h-4 w-4 shrink-0 text-muted" aria-label={step.done === null ? "Manual step" : "Not done"} />
						)}
						<div>
							<p className="text-sm font-medium text-foreground">{step.label}</p>
							<p className="text-xs text-muted">{step.detail}</p>
						</div>
					</li>
				))}
			</ol>
		</SectionBlock>
	);
}
